"use client";

import { useState, useCallback } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Calculator, TrendingUp, PiggyBank, Clock, ArrowRight } from "lucide-react";

/* ── helpers ── */
function fmt(n: number) {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  suffix = "",
  prefix = "",
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  prefix?: string;
  onChange: (v: number) => void;
}) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-sm text-gray-300">{label}</label>
        <span className="text-sm font-semibold text-white tabular-nums">
          {prefix}
          {value.toLocaleString("nl-NL")}
          {suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-2 w-full cursor-pointer appearance-none rounded-full [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white/20 [&::-moz-range-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white/20 [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-blue-500/40 [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
        style={{
          background: `linear-gradient(to right, #3b82f6 0%, #6366f1 ${pct}%, rgba(255,255,255,0.05) ${pct}%)`,
        }}
        aria-label={label}
      />
      <div className="mt-1 flex justify-between text-[11px] text-gray-500">
        <span>
          {prefix}
          {min.toLocaleString("nl-NL")}
          {suffix}
        </span>
        <span>
          {prefix}
          {max.toLocaleString("nl-NL")}
          {suffix}
        </span>
      </div>
    </div>
  );
}

function ResultCard({
  icon: Icon,
  label,
  value,
  sublabel,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sublabel: string;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4">
      <div className="mb-2 flex items-center gap-2">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-sm text-gray-400">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="mt-1 text-[11px] text-gray-500">{sublabel}</div>
    </div>
  );
}

/* ── constants ── */
const TRAINING_FREQ_OPTIONS = [
  { label: "Niet structureel", sessionsPerYear: 0, hoursPerSession: 0 },
  { label: "Jaarlijks", sessionsPerYear: 1, hoursPerSession: 8 },
  { label: "Per kwartaal", sessionsPerYear: 4, hoursPerSession: 4 },
  { label: "Maandelijks", sessionsPerYear: 12, hoursPerSession: 2 },
];

export default function RoiCalculator() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [tracked, setTracked] = useState(false);

  const [teamSize, setTeamSize] = useState(15);
  const [freqIndex, setFreqIndex] = useState(2); // kwartaal
  const [trainerRate, setTrainerRate] = useState(250);
  const [dealValue, setDealValue] = useState(5000);
  const [closeRate, setCloseRate] = useState(25);

  const trackInteraction = useCallback(() => {
    if (tracked) return;
    setTracked(true);
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "roi_calculator_used", { event_category: "engagement" });
    }
  }, [tracked]);

  const handleChange = useCallback(
    (setter: (v: number) => void) => (v: number) => {
      setter(v);
      trackInteraction();
    },
    [trackInteraction],
  );

  /* ── calculations ── */
  const freq = TRAINING_FREQ_OPTIONS[freqIndex];

  // Kostenbesparing
  const currentYearlyCost =
    freq.sessionsPerYear * freq.hoursPerSession * trainerRate * (teamSize / 5); // groepen van 5
  const currentQuarterlyCost = currentYearlyCost / 4;
  const savingsPerQuarter = Math.round(currentQuarterlyCost * 0.7); // 70% besparing

  // Extra omzet
  const closeRateImprovement = 0.1; // +10% close rate verbetering
  const dealsPerPersonPerQuarter = 12; // gemiddeld
  const currentRevenue = teamSize * dealsPerPersonPerQuarter * dealValue * (closeRate / 100);
  const improvedRevenue =
    teamSize *
    dealsPerPersonPerQuarter *
    dealValue *
    ((closeRate + closeRate * closeRateImprovement) / 100);
  const extraRevenue = Math.round(improvedRevenue - currentRevenue);

  // Snellere onboarding
  const avgOnboardingWeeks = 12;
  const weeksSaved = Math.round(avgOnboardingWeeks * 0.3); // 30% sneller

  // Totaal
  const totalQuarterlyROI = savingsPerQuarter + extraRevenue;

  return (
    <section ref={ref} className="py-10 md:py-20" aria-labelledby="roi-heading">
      <div className="mx-auto max-w-6xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-8 text-center md:mb-14">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-4 py-1.5 text-sm text-blue-300">
              <Calculator className="h-4 w-4" aria-hidden="true" />
              ROI Calculator
            </div>
            <h2 id="roi-heading" className="text-3xl font-bold md:text-4xl">
              Bereken jouw{" "}
              <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                besparing en extra omzet
              </span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-gray-300">
              Vul je teamgegevens in en zie direct wat AI-gesprekstraining oplevert — in
              kostenbesparing en hogere omzet.
            </p>
          </div>
        </motion.div>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* Left: Inputs */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="space-y-6 rounded-2xl border border-white/5 bg-white/[0.02] p-6 md:p-8"
          >
            <h3 className="mb-2 text-lg font-semibold text-white">Jouw situatie</h3>

            <Slider
              label="Teamgrootte"
              value={teamSize}
              min={5}
              max={100}
              step={5}
              suffix=" medewerkers"
              onChange={handleChange(setTeamSize)}
            />

            <div>
              <label className="mb-2 block text-sm text-gray-300">
                Huidige trainingsfrequentie
              </label>
              <div className="grid grid-cols-2 gap-2">
                {TRAINING_FREQ_OPTIONS.map((opt, i) => (
                  <button
                    key={opt.label}
                    onClick={() => {
                      setFreqIndex(i);
                      trackInteraction();
                    }}
                    className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                      freqIndex === i
                        ? "border-blue-500/50 bg-blue-500/10 text-blue-300"
                        : "border-white/5 bg-white/[0.02] text-gray-400 hover:border-white/10 hover:text-gray-300"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <Slider
              label="Uurtarief externe trainer"
              value={trainerRate}
              min={100}
              max={500}
              step={25}
              prefix="€"
              suffix="/uur"
              onChange={handleChange(setTrainerRate)}
            />

            <Slider
              label="Gemiddelde dealwaarde"
              value={dealValue}
              min={500}
              max={50000}
              step={500}
              prefix="€"
              onChange={handleChange(setDealValue)}
            />

            <Slider
              label="Huidige close rate"
              value={closeRate}
              min={5}
              max={80}
              step={5}
              suffix="%"
              onChange={handleChange(setCloseRate)}
            />
          </motion.div>

          {/* Right: Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            <div className="rounded-2xl border border-blue-500/20 bg-gradient-to-br from-blue-500/10 to-indigo-500/5 p-6 md:p-8">
              <h3 className="mb-1 text-lg font-semibold text-white">Geschatte ROI per kwartaal</h3>
              <div className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-4xl font-extrabold text-transparent md:text-5xl">
                {fmt(totalQuarterlyROI)}
              </div>
              <p className="mt-2 text-sm text-gray-400">
                Kostenbesparing + extra omzet door beter getraind team
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <ResultCard
                icon={PiggyBank}
                label="Besparing op trainingskosten"
                value={fmt(savingsPerQuarter)}
                sublabel={`${fmt(currentQuarterlyCost)} huidig → 70% besparing`}
                color="bg-green-500/20 text-green-400"
              />
              <ResultCard
                icon={TrendingUp}
                label="Extra omzet door hogere close rate"
                value={fmt(extraRevenue)}
                sublabel={`+10% close rate door dagelijks oefenen`}
                color="bg-blue-500/20 text-blue-400"
              />
              <ResultCard
                icon={Clock}
                label="Snellere onboarding"
                value={`${weeksSaved} weken sneller`}
                sublabel="30% snellere ramp-time voor nieuwe medewerkers"
                color="bg-purple-500/20 text-purple-400"
              />
              <ResultCard
                icon={TrendingUp}
                label="Trainingsfrequentie"
                value="3x meer"
                sublabel="Medewerkers oefenen 3x vaker dan met traditionele training"
                color="bg-indigo-500/20 text-indigo-400"
              />
            </div>

            <div className="pt-4 text-center">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition-[box-shadow,filter,transform] hover:shadow-blue-500/40 hover:brightness-110 active:scale-[0.98]"
              >
                Ontdek de mogelijkheden
                <ArrowRight className="h-4 w-4" />
              </a>
              <p className="mt-3 text-[11px] text-gray-500">
                Berekeningen zijn indicatief op basis van marktgemiddelden en CloserAI-pilotdata
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
